import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, RefreshCw, FileWarning, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, StatCard, EmptyState, LoadingBlock, StatusBadge } from "../../components/app/ui";
import { adminApi, errorMessage, formatDate, roleLabel } from "../../lib/api";

const DOC_LABEL = {
  selfie: "Selfie", driver_license: "Driver's License", vehicle_photo: "Vehicle Photo",
  vehicle_ownership: "Vehicle RC", insurance: "Insurance", pollution: "Pollution (PUC)",
  fssai_license: "FSSAI License", ownership_document: "Ownership Doc", gst_certificate: "GST Certificate",
  owner_id: "Owner ID", taxi_permit: "Taxi Permit",
};

const reviewHref = (u) => (u.role === "restaurant" ? `/app/admin/verify/restaurants?user=${u.user_id}` : `/app/admin/verify/commercial-drivers?user=${u.user_id}`);

export default function DocsPending() {
  const [users, setUsers] = useState(null);

  const load = () => {
    setUsers(null);
    adminApi.verifications({ status: "verified", docs_pending: true }).then(setUsers).catch((e) => { toast.error(errorMessage(e)); setUsers([]); });
  };
  useEffect(() => { load(); }, []);

  const list = users || [];
  return (
    <>
      <PageHeader
        title={<span className="inline-flex items-center gap-3">Documents pending{list.length > 0 && <span className="rounded-full bg-sky-600 px-2.5 py-0.5 text-sm font-bold text-white" data-testid="docspending-count">{list.length}</span>}</span>}
        description="Partners who went live via offline KYC and still owe documents. Open one to upload on their behalf."
        actions={<Button variant="outline" onClick={load} className="rounded-full" data-testid="docspending-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>}
      />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatCard label="Awaiting documents" value={users === null ? "…" : list.length} icon={FileWarning} tone="brand" />
        <StatCard label="Drivers" value={users === null ? "…" : list.filter((u) => u.role === "driver").length} icon={Clock} tone="ink" />
        <StatCard label="Restaurants" value={users === null ? "…" : list.filter((u) => u.role === "restaurant").length} icon={Clock} tone="ink" />
      </div>
      {users === null && <LoadingBlock rows={4} />}
      {users && list.length === 0 && <EmptyState icon={FileWarning} title="Nothing pending" text="Every live partner has submitted all their documents." />}
      {list.length > 0 && (
        <div className="kt-card overflow-hidden">
          <table className="w-full text-sm" data-testid="docspending-table">
            <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-5 py-3 font-semibold">Partner</th><th className="px-5 py-3 font-semibold">Role</th><th className="hidden px-5 py-3 font-semibold md:table-cell">Missing documents</th><th className="hidden px-5 py-3 font-semibold lg:table-cell">Went live</th><th className="px-5 py-3" /></tr></thead>
            <tbody className="divide-y divide-surface-200">
              {list.map((u) => (
                <tr key={u.user_id} className="transition-colors hover:bg-surface-50" data-testid={`docspending-row-${u.user_id}`}>
                  <td className="px-5 py-4"><p className="font-medium text-ink">{u.name}</p>{u.role === "restaurant" && u.restaurant_name && <p className="text-xs font-medium text-brand">{u.restaurant_name}</p>}<p className="text-xs text-ink-soft">{u.email?.endsWith("@phone.local") ? u.phone : u.email}</p></td>
                  <td className="px-5 py-4 text-ink-soft">{roleLabel(u.role)}</td>
                  <td className="hidden px-5 py-4 md:table-cell"><div className="flex flex-wrap gap-1.5">{(u.docs_pending_list || []).map((d) => <span key={d} className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800">{DOC_LABEL[d] || d}</span>)}</div></td>
                  <td className="hidden px-5 py-4 text-ink-soft lg:table-cell">{u.verified_at ? formatDate(u.verified_at) : "—"}</td>
                  <td className="px-5 py-4 text-right"><Link to={reviewHref(u)}><Button size="sm" className="rounded-full bg-sky-600 text-white hover:bg-sky-700" data-testid={`docspending-open-${u.user_id}`}><Upload className="mr-1.5 h-3.5 w-3.5" /> Upload docs</Button></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
